import { useEffect, useState } from "react";
import Select from "react-select";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

/**
 * CountryCodeSelect Component
 * A reusable dropdown component for selecting country dialing codes
 *
 * @param {Object} props
 * @param {string} props.value - Default country code (e.g., "+94")
 * @param {function} props.onChange - Callback function when selection changes, receives the selected code
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.isRequired - Whether the field is required (default: false)
 * @param {string} props.width - Width of the dropdown
 * @param {object} props.customStyles - Additional custom styles for the Select component
 */
export default function CountryCodes({
  value,
  onChange,
  placeholder = "Code",
  isRequired = false,
  width = "100px",
  customStyles = {},
}) {
  const [countries, setCountries] = useState([
    { value: "+94", label: "Sri Lanka (+94)", country: "Sri Lanka" },
  ]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchCountries() {
      try {
        const res = await fetch(
          `${BASE_URL}/api/admin/countries/get-countries`
        );
        if (!res.ok) throw new Error("Countries API error");
        const data = await res.json();
        if (data && data.success && data.data) {
          const countryOptions = data.data.map((country) => ({
            value: country.international_dialing,
            label: `${country.country} (${country.international_dialing})`,
            country: country.country,
          }));
          setCountries(countryOptions);
        }
      } catch (err) {
        console.error("Error fetching countries:", err);
      } finally {
        setIsLoading(false);
      }
    }
    fetchCountries();
  }, []);

  return (
    <div style={{ width: width }}>
      <Select
        value={countries.find((c) => c.value === value)}
        onChange={(selected) => onChange(selected.value)}
        options={countries}
        isSearchable={true}
        isLoading={isLoading}
        placeholder={placeholder}
        className="react-select-container"
        classNamePrefix="react-select"
        required={isRequired}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        formatOptionLabel={(option, { context }) => {
          // Show country name and dial code in dropdown menu
          if (context === "menu") {
            return (
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <span style={{ flex: 1 }}>{option.country}</span>
                <span style={{ color: "#666", fontSize: "13px" }}>
                  {option.value}
                </span>
              </div>
            );
          }
          // Show dial code in selected value
          return <span style={{ fontSize: "13px" }}>{option.value}</span>;
        }}
        styles={{
          control: (base) => ({
            ...base,
            height: "48px",
            minHeight: "48px",
            fontSize: "13px",
            borderRadius: "12px",
            backgroundColor: "#f7fafc",
            border: "2px solid #e2e8f0",
            ...customStyles.control,
          }),
          valueContainer: (base) => ({
            ...base,
            height: "48px",
            padding: "0 8px",
            ...customStyles.valueContainer,
          }),
          input: (base) => ({
            ...base,
            margin: "0",
            padding: "0",
            ...customStyles.input,
          }),
          indicatorsContainer: (base) => ({
            ...base,
            height: "48px",
            ...customStyles.indicatorsContainer,
          }),
          option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected
              ? "#4299e1"
              : state.isFocused
              ? "#e6f2ff"
              : "white",
            color: state.isSelected ? "white" : "#2d3748",
            cursor: "pointer",
            padding: "10px 12px",
            ...customStyles.option,
          }),
          menu: (base) => ({
            ...base,
            minWidth: "300px",
            ...customStyles.menu,
          }),
          menuPortal: (base) => ({
            ...base,
            zIndex: 99999,
            ...customStyles.menuPortal,
          }),
          singleValue: (base) => ({
            ...base,
            display: "flex",
            alignItems: "center",
            ...customStyles.singleValue,
          }),
        }}
      />
    </div>
  );
}
